import React from 'react';

import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useKnzDocument } from '../../KnzDocumentContext';
import { KnzComponentProps } from '../../types';

/**
 * 自動目次（WorkflowTableOfContents ベース）。
 * ドキュメント内の Section ブロック（title / anchor）から目次を自動生成する。
 * params: title（任意, 目次見出し。デフォルト「目次」）
 */
export const Toc: React.FC<KnzComponentProps> = ({ node }) => {
  const { roots, tocMode } = useKnzDocument();
  const heading = (node.params.title as string) || '目次';

  // hoist（右カラムへ昇格）/ ignore（無視）のときは本文中には描画しない。
  // hoist の場合は KnzViewer が Section を収集して右カラムに目次を出す。
  if (tocMode !== 'inline') return null;

  // ルート直下および Section 配下の Section を収集する
  const items: { label: string; anchor: string }[] = [];
  const collect = (nodes: typeof roots) => {
    for (const n of nodes) {
      if (n.name === 'Section') {
        const anchor = n.params.anchor as string | undefined;
        const title = (n.params.title as string) ?? '';
        if (anchor && title) items.push({ label: title, anchor });
        collect(n.children);
      }
    }
  };
  collect(roots);

  if (items.length === 0) return null;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}
      >
        <ListAltOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {heading}
        </Typography>
      </Stack>
      <Stack spacing={0} sx={{ px: 2, py: 1 }}>
        {items.map((item, i) => (
          <Link
            key={i}
            href={`#${item.anchor}`}
            underline="none"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(item.anchor)?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              px: 1,
              borderRadius: 1,
              transition: 'background-color 0.15s',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {item.label}
            </Typography>
          </Link>
        ))}
      </Stack>
    </Box>
  );
};

export default Toc;
